// @ts-check
/**
 * Triggers tab: the four automated WhatsApp messages (order confirmed / shipped / delivered, lead
 * captured), each configured and switched on independently. One shared initial load (triggers +
 * approved templates) surfaced through a persistent, dismissible error banner (shown for a first
 * load or any later reload failure, cards stay mounted underneath it); a per-card save/enable/
 * disable failure is a toast, never a full-tab error (see useTriggers.js for the load/mutation
 * logic).
 */
import { RefreshCw, X } from 'lucide-react';
import { useState } from 'react';
import { WHATSAPP_TRIGGER_KEY } from '../../utils/whatsapp';
import LoadingScreen from '../LoadingScreen';
import TriggerCard from './TriggerCard';
import { useTriggers } from './useTriggers';

/** Fixed, sensible display order regardless of what the API returns them in. */
const TRIGGER_ORDER = /** @type {string[]} */ ([
  WHATSAPP_TRIGGER_KEY.ORDER_CONFIRMED,
  WHATSAPP_TRIGGER_KEY.ORDER_SHIPPED,
  WHATSAPP_TRIGGER_KEY.ORDER_DELIVERED,
  WHATSAPP_TRIGGER_KEY.LEAD_CAPTURED,
]);

const TriggersTab = () => {
  const { triggers, templates, loading, error, reload, saveTrigger, setEnabled } = useTriggers();
  const [dismissed, setDismissed] = useState(false);

  // Un-dismiss the moment a new attempt STARTS, not when it resolves: setError() with the exact
  // same message as before (e.g. a repeat "Network Error") would not itself trigger a re-render,
  // so waiting for the error to "change" can leave a dismissed banner silently stuck hidden through
  // a second, still-failing reload.
  const handleReload = () => {
    setDismissed(false);
    reload();
  };

  const ordered = triggers
    ? [...triggers].sort((a, b) => TRIGGER_ORDER.indexOf(a.key) - TRIGGER_ORDER.indexOf(b.key))
    : null;

  return (
    <div className="space-y-4 max-w-full">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-medium text-foreground">Triggers</h2>
          <p className="text-sm text-muted-foreground">
            Automated messages sent to customers as their order (or a captured lead) moves along.
          </p>
        </div>
        {/* Not disabled while a load is in flight: an admin refreshing again mid-load is exactly
            what the ticket guard in useTriggers.js exists to make safe (the older response is
            dropped when a newer one lands first). */}
        <button
          type="button"
          onClick={handleReload}
          aria-label="Refresh triggers"
          className="p-2 border border-input rounded-lg hover:bg-muted/50"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Shown for a first-load failure (no cards yet) or a later reload failure (cards from the
          last good load stay visible and interactive underneath - a failed refresh must never
          make the admin think stale switches on screen are still current). */}
      {error && !dismissed && (
        <div
          role="alert"
          className="flex items-start justify-between gap-3 px-4 py-3 text-sm border rounded-lg bg-red-50 dark:bg-red-950/50 border-red-200 dark:border-red-900"
        >
          <p className="text-red-800 dark:text-red-300 break-words">{error}</p>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={handleReload}
              className="px-3 py-1 text-sm text-white bg-red-700 rounded-lg hover:bg-red-800"
            >
              Retry
            </button>
            <button
              type="button"
              onClick={() => setDismissed(true)}
              aria-label="Dismiss error"
              className="p-1 text-red-700 dark:text-red-300 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/60"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {ordered === null && loading && <LoadingScreen />}

      {ordered !== null && (
        <div className="space-y-4">
          {ordered.map((trigger) => (
            <TriggerCard
              key={trigger.key}
              trigger={trigger}
              templates={templates ?? []}
              onSave={saveTrigger}
              onSetEnabled={setEnabled}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default TriggersTab;
